/**
 * BatchCard Component
 *
 * Displays a single production batch (shadow or real) within the planner grid.
 * Shows batch details and approval controls for shadow batches.
 *
 * US-C2-006: Planner Page UI
 * US-C2-011: Planner -> Timeline navigation link
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { usePlanApproval } from '@/hooks/usePlanApproval'
import { Check, X, Loader2, Box, Calendar, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { parseDateString } from '@/utils/dateUtils'

export default function BatchCard({ batch, onUpdate }) {
  const { approveBatch, rejectBatch, approving } = usePlanApproval()
  const [isProcessing, setIsProcessing] = useState(false)

  const isShadow = batch.is_shadow
  const isPending = batch.approval_status === 'pending'
  const isRejected = batch.approval_status === 'rejected'
  const isApproved = batch.approval_status === 'approved'

  const estimatedYield = batch.target_kg_fixed
    ? parseFloat(batch.target_kg_fixed)
    : (batch.total_blocks || 0) * 0.3

  const handleApprove = async () => {
    setIsProcessing(true)
    try {
      await approveBatch(batch.id)
      onUpdate?.()
    } catch (err) {
      console.error('Failed to approve batch:', err)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReject = async () => {
    setIsProcessing(true)
    try {
      await rejectBatch(batch.id)
      onUpdate?.()
    } catch (err) {
      console.error('Failed to reject batch:', err)
    } finally {
      setIsProcessing(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return parseDateString(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    })
  }

  return (
    <Card
      className={cn(
        'p-3 transition-all duration-200',
        isShadow && 'border-dashed border-2',
        isShadow && isPending && 'bg-amber-50/30 border-amber-300 hover:bg-amber-50/50',
        isShadow && isRejected && 'bg-red-50/30 border-red-300 opacity-60',
        !isShadow && 'bg-white hover:shadow-md'
      )}
    >
      <div className="space-y-2">
        {/* Header: Batch code + Status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {batch.batch_code}
            </p>
            {batch.manually_adjusted && (
              <Badge variant="outline" className="text-xs mt-1">
                Adjusted
              </Badge>
            )}
          </div>
          {isShadow && (
            <Badge
              variant={isPending ? 'default' : isRejected ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {batch.approval_status}
            </Badge>
          )}
        </div>

        {/* Details: Blocks + Estimated yield */}
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <Box className="h-3 w-3" />
            <span>{batch.total_blocks} blocks</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(batch.inoculation_date)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>
            Est. yield: <span className="font-medium">{estimatedYield.toFixed(1)}kg</span>
          </span>
          {/* US-C2-011: Planner -> Timeline link */}
          <Link
            to={`/production/timeline?batch=${encodeURIComponent(batch.batch_code)}`}
            className="text-primary hover:underline flex items-center gap-0.5"
            title="View on Timeline"
          >
            Timeline
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Actions for shadow batches */}
        {isShadow && isPending && (
          <div className="flex gap-2 pt-2 border-t">
            <Button
              size="sm"
              variant="outline"
              onClick={handleReject}
              disabled={isProcessing || approving}
              className="flex-1 h-7 text-xs"
            >
              {isProcessing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <X className="h-3 w-3 mr-1" />
                  Reject
                </>
              )}
            </Button>
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={isProcessing || approving}
              className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
            >
              {isProcessing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Approve
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
